import { useCallback, useEffect, useMemo, useState } from 'react';
import { ErroApi } from '../lib/api';
import { buscarCalendarioEscalas, CORES_STATUS, NOME_STATUS, type EventoCalendario } from '../lib/escalas';
import { diaDe, gradeDoMes, horaDe, HOJE_SP, mesAtual, nomeDoMes, somarMeses } from '../lib/agenda';
import { Alerta, Cartao } from './Interface';

const DIAS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];

export default function CalendarioEscalas({
  ministerio,
  nomeMinisterio,
  aoEscolherEvento,
  buscar,
  semEscala = 'ninguém escalado — toque para montar',
  versao = 0,
}: {
  ministerio: string;
  nomeMinisterio: string;
  aoEscolherEvento: (id: number) => void;
  /** outra fonte para o mes (a escala de lideres tem rota propria) */
  buscar?: (mes: string) => Promise<{ eventos: EventoCalendario[] }>;
  /** o que dizer no culto vazio — "toque para montar" so vale para quem monta */
  semEscala?: string;
  /** muda quando alguem mexeu na escala por fora: recarrega sem perder o mes */
  versao?: number;
}) {
  const [mes, setMes] = useState(mesAtual());
  const [eventos, setEventos] = useState<EventoCalendario[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [diaEscolhido, setDiaEscolhido] = useState(HOJE_SP());

  const carregar = useCallback(async () => {
    try {
      const d = buscar ? await buscar(mes) : await buscarCalendarioEscalas(ministerio, mes);
      setEventos(d.eventos);
      setErro(null);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui carregar o calendário.');
    }
    // versao nao e usada dentro: entra so para disparar a recarga
  }, [ministerio, mes, buscar, versao]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const porDia = useMemo(() => {
    const mapa = new Map<string, EventoCalendario[]>();
    for (const e of eventos) {
      if (!e.inicio) continue;
      const dia = diaDe(e.inicio);
      const lista = mapa.get(dia) ?? [];
      lista.push(e);
      mapa.set(dia, lista);
    }
    return mapa;
  }, [eventos]);

  const doDia = porDia.get(diaEscolhido) ?? [];

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
      <Cartao>
        {erro && (
          <div className="mb-3">
            <Alerta>{erro}</Alerta>
          </div>
        )}

        <div className="mb-4 flex items-center justify-between">
          <button
            onClick={() => setMes(somarMeses(mes, -1))}
            className="rounded-lg px-3 py-1 text-tinta-400 hover:bg-tinta-100"
          >
            &larr;
          </button>
          <h3 className="font-semibold text-tinta-900">
            {nomeDoMes(mes)} · {nomeMinisterio}
          </h3>
          <button
            onClick={() => setMes(somarMeses(mes, 1))}
            className="rounded-lg px-3 py-1 text-tinta-400 hover:bg-tinta-100"
          >
            &rarr;
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold uppercase text-tinta-400">
          {DIAS.map((d) => (
            <span key={d}>{d}</span>
          ))}
        </div>

        <div className="mt-1 grid grid-cols-7 gap-1">
          {gradeDoMes(mes).map(({ dia, doMes }) => {
            const doDiaAqui = porDia.get(dia) ?? [];
            const hoje = dia === HOJE_SP();
            const escolhido = dia === diaEscolhido;
            const faltando = doDiaAqui.some((e) => e.escalados.length === 0);
            return (
              <button
                key={dia}
                onClick={() => setDiaEscolhido(dia)}
                className={`min-h-[4.5rem] rounded-lg border p-1 text-left transition ${
                  escolhido ? 'border-tinta-900 bg-tinta-900/5' : 'border-transparent hover:border-tinta-200'
                } ${doMes ? '' : 'opacity-40'}`}
              >
                <span className="flex items-center justify-between">
                  <span
                    className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs ${
                      hoje ? 'bg-tinta-900 font-bold text-white' : 'text-tinta-600'
                    }`}
                  >
                    {Number(dia.slice(8))}
                  </span>
                  {faltando && doMes && (
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-500" title="Evento sem escala" />
                  )}
                </span>
                <span className="mt-1 block space-y-0.5">
                  {doDiaAqui.slice(0, 2).map((e) => (
                    <span
                      key={e.id}
                      className="block truncate rounded bg-tinta-100 px-1 text-[10px] text-tinta-700"
                    >
                      {e.escalados.length > 0 ? `${e.escalados.length}· ` : ''}
                      {e.titulo}
                    </span>
                  ))}
                  {doDiaAqui.length > 2 && (
                    <span className="block px-1 text-[10px] text-tinta-400">
                      +{doDiaAqui.length - 2}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>

        <p className="mt-3 text-xs text-tinta-400">
          O ponto laranja marca culto sem ninguém escalado.
        </p>
      </Cartao>

      <Cartao>
        <h3 className="font-semibold text-tinta-900">
          {new Intl.DateTimeFormat('pt-BR', {
            timeZone: 'UTC',
            day: '2-digit',
            month: 'long',
          }).format(new Date(`${diaEscolhido}T12:00:00Z`))}
        </h3>

        {doDia.length === 0 ? (
          <p className="mt-2 text-sm text-tinta-400">Nenhum evento neste dia.</p>
        ) : (
          <ul className="mt-3 space-y-4">
            {doDia.map((e) => (
              <li key={e.id}>
                <button
                  onClick={() => aoEscolherEvento(e.id)}
                  className="w-full rounded-xl border border-tinta-200 p-3 text-left transition hover:bg-tinta-100"
                >
                  <span className="block font-medium text-tinta-900">{e.titulo}</span>
                  <span className="block text-xs text-tinta-400">
                    {e.inicio ? horaDe(e.inicio) : ''}
                    {e.local ? ` · ${e.local}` : ''}
                  </span>

                  {e.escalados.length === 0 ? (
                    <span className="mt-2 block text-xs text-amber-700">
                      {semEscala}
                    </span>
                  ) : (
                    <span className="mt-2 block space-y-1">
                      {e.escalados.map((s) => (
                        <span key={s.id} className="flex items-center justify-between gap-2 text-xs">
                          <span className="truncate text-tinta-700">
                            <b className="font-medium">{s.funcao}:</b> {s.membroNome}
                          </span>
                          <span
                            className={`shrink-0 rounded px-1.5 py-0.5 ${CORES_STATUS[s.status] ?? ''}`}
                          >
                            {NOME_STATUS[s.status] ?? s.status}
                          </span>
                        </span>
                      ))}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </Cartao>
    </div>
  );
}
